import agentWebSocket, { type ConnectionState } from "@app/config/agentWebSocket";
import type { AgentExtraItem } from "@app/config/agentWebSocket";
import { queryClient } from "@app/config/api/api";
import {
  agentSessionDocumentsOptions,
  agentSessionMessagesOptions,
  translateBackendMessages,
  useAgentSessionMessages
} from "@app/queries/agent";
import { useAuth0 } from "@auth0/auth0-react";
import type { AgentServerMessage, ExperimentId } from "@globals";
import type { ChatMessage, CommittedDocuments } from "@lib/agent/types";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";

let messageIdCounter = 0;
function nextId(): string {
  return `msg-${++messageIdCounter}`;
}

// Index of the last assistant message, or -1. The chunk/error handlers target the streaming
// bubble by role + position rather than object identity (which would break if the array were
// ever cloned). Manual loop instead of Array.findLastIndex — the app tsconfig targets lib ES2022.
function lastAssistantIndex(messages: ChatMessage[]): number {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === "assistant") {
      return i;
    }
  }
  return -1;
}

export function useAgentChat(experimentId: ExperimentId, teamId: string | null) {
  const [isStreaming, setIsStreaming] = useState(false);
  // Optimistic flag set the instant the user clicks Stop. Backend cancellation is cooperative —
  // the worker only notices the cancel flag at the next stream message boundary, which can take
  // several seconds — so without this the Stop button gives no feedback and gets spammed. Cleared
  // wherever the turn settles (turn.done, error, reconnect) or a fresh turn starts.
  const [isStopping, setIsStopping] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>(agentWebSocket.state);
  const isReady = connectionState === "connected";
  // Messages belonging to the current live turn (≤ 2 items: user prompt + streaming assistant).
  // Cleared on agent.turn.done and reconnect.
  const [liveMessages, setLiveMessages] = useState<ChatMessage[]>([]);
  // Extras from the most recent agent.extras message — streamed before agent.turn.done,
  // persisted in memory so suggestions remain visible after the turn completes.
  // Lost on page refresh / reconnect. Cleared on new send or clearLocal.
  const [liveExtras, setLiveExtras] = useState<AgentExtraItem[] | undefined>(undefined);
  // agent_session_id from the most recent turn; falls back to persisted historyData.
  const [liveAgentSessionId, setLiveAgentSessionId] = useState<string | null>(null);

  const { getAccessTokenSilently } = useAuth0();

  const {
    data: historyData,
    isLoading: isHistoryLoading,
    isError: historyError,
    refetch: refetchHistory
  } = useAgentSessionMessages(experimentId);

  // allMessages is the single derived view: persisted history + any live-turn messages.
  // historyData.messages only contains committed messages — the current assistant reply is not
  // written to the DB until AgentCore finishes streaming it, so in-progress chunk content is
  // never present in history. A page refresh mid-turn restores the spinner but not the partial
  // text; new chunks stream in normally from wherever the agent currently is in its turn.
  const allMessages = useMemo(() => {
    const serverMessages = translateBackendMessages(historyData?.messages ?? []);
    return [...serverMessages, ...liveMessages];
  }, [historyData, liveMessages]);

  const agentSessionId = liveAgentSessionId ?? historyData?.agent_session_id ?? null;
  const committedMessageCount = historyData?.messages?.length ?? 0;

  const clearLiveState = useCallback(() => {
    setLiveMessages([]);
    setLiveExtras(undefined);
    setLiveAgentSessionId(null);
  }, []);

  const clearLocal = useCallback(() => {
    clearLiveState();
    setIsStreaming(false);
    setIsStopping(false);
  }, [clearLiveState]);

  // Connect on mount, reconnect when the experiment's team changes
  useEffect(() => {
    void agentWebSocket.connect(() => getAccessTokenSilently(), teamId);
    return () => agentWebSocket.close();
  }, [teamId, getAccessTokenSilently]);

  // Track connection state
  useEffect(() => agentWebSocket.onStateChange(setConnectionState), []);

  // On (re)connect: reset all local state and refetch server history. Using clearLocal (not
  // clearLiveState) so isStreaming is reset too — a turn that finished while disconnected never
  // delivers agent.turn.done, so without this the spinner would stay up forever. If the turn is
  // still running, the is_turn_in_progress effect below immediately re-sets isStreaming to true.
  useEffect(() => {
    if (!isReady) {
      return;
    }
    clearLocal();
    void queryClient.invalidateQueries({
      queryKey: agentSessionMessagesOptions(experimentId).queryKey
    });
  }, [isReady, clearLocal, experimentId]);

  // When the server reports a turn still running (reconnected mid-turn from a previous session),
  // show the streaming spinner with a pending assistant bubble until agent.turn.done arrives.
  // Declared after the isReady effect so React runs clearLocal() first in the same render cycle,
  // then this effect restores isStreaming. isReady is a dep so reconnect re-triggers this even
  // when is_turn_in_progress stays true (its value would not have changed otherwise).
  useEffect(() => {
    if (!isReady || !historyData?.is_turn_in_progress) {
      return;
    }
    setIsStreaming(true);
    setLiveMessages(prev => {
      if (prev.some(m => m.role === "assistant")) {
        return prev;
      }
      return [{ id: nextId(), role: "assistant", content: "" }];
    });
  }, [isReady, historyData?.is_turn_in_progress]);

  // Handle server messages
  useEffect(() => {
    const handleMessage = (message: AgentServerMessage) => {
      switch (message.action) {
        case "agent.response.chunk": {
          // Functional update: prev always reflects latest state, no external buffer needed.
          setLiveMessages(prev => {
            const idx = lastAssistantIndex(prev);
            if (idx === -1) {
              return prev;
            }
            return prev.map((m, i) => (i === idx ? { ...m, content: m.content + message.payload.message } : m));
          });
          break;
        }
        case "agent.tool_use": {
          // Any tool call may be followed by more streamed text; append a paragraph break so
          // subsequent chunks start on a new line instead of running onto preceding text.
          setLiveMessages(prev => {
            const idx = lastAssistantIndex(prev);
            if (idx === -1) {
              return prev;
            }
            return prev.map((m, i) => (i === idx ? { ...m, content: m.content + "\n\n" } : m));
          });
          break;
        }
        case "agent.extras": {
          setLiveExtras(message.payload.items);
          break;
        }
        case "agent.turn.done": {
          if (message.payload.experiment_id !== experimentId) {
            break;
          }
          // Clear liveMessages only once the refetch succeeds: fetchQuery resolves after the
          // cache is written, so the committed turn is renderable from server history before the
          // overlay disappears (no flash). On failure we deliberately keep liveMessages — server
          // history lacks the new turn, so there is nothing to dedupe against and clearing would
          // just blank the content the user watched stream in. The next send/reconnect resets it.
          setLiveAgentSessionId(message.payload.agent_session_id ?? null);
          setIsStreaming(false);
          setIsStopping(false);
          void queryClient
            .fetchQuery(agentSessionMessagesOptions(experimentId))
            .then(() => {
              setLiveMessages([]);
            })
            .catch(() => {
              // Keep the streamed turn visible; do not blank it on a transient refetch error.
            });
          break;
        }
        case "agent.error": {
          // Intentionally does NOT refetch committed history. The error text is client-only
          // (never persisted), so it lives solely in this overlay; the user prompt, however, was
          // already committed by AgentCore. Calling fetchQuery here would pull that committed
          // prompt into historyData while the overlay still holds the same prompt, re-creating the
          // duplicate bubble that refetchOnWindowFocus/Reconnect/Mount: false was added to prevent.
          // The errored exchange therefore stays in the overlay until the next send replaces it.
          setLiveMessages(prev => {
            const idx = lastAssistantIndex(prev);
            if (idx !== -1) {
              return prev.map((m, i) => (i === idx ? { ...m, content: m.content || message.payload.message } : m));
            }
            return [...prev, { id: nextId(), role: "assistant", content: message.payload.message }];
          });
          setIsStreaming(false);
          setIsStopping(false);
          break;
        }
      }
    };

    return agentWebSocket.onServerMessage(handleMessage);
  }, [experimentId]);

  const send = useCallback(
    // Returns whether the prompt was actually dispatched. Callers that commit side effects before
    // sending (e.g. uploading documents) need this to avoid clearing their draft when the frame
    // was dropped on a not-ready socket.
    (content: string, attachments?: { filenames?: string[] }): boolean => {
      if (!content.trim() || isStreaming || isHistoryLoading || historyError) {
        return false;
      }
      // The input is gated on `isReady` at the component layer; this guard catches the
      // race where the WS drops between render and send so the frame isn't silently
      // discarded on a dead socket. The user retries once the auto-reconnect catches up.
      if (!isReady) {
        return false;
      }

      const filenames = attachments?.filenames ?? [];

      const userMessage: ChatMessage = {
        id: nextId(),
        role: "user",
        content,
        attachments: filenames.length > 0 ? filenames.map(filename => ({ filename })) : undefined
      };
      const assistantMessage: ChatMessage = { id: nextId(), role: "assistant", content: "" };

      setLiveMessages([userMessage, assistantMessage]);
      setLiveExtras(undefined);
      setIsStreaming(true);
      setIsStopping(false);

      // Documents are uploaded out-of-band via the REST endpoint and attached to the session; the
      // server hands the agent the session's full document set each turn, so the prompt frame
      // carries no handles. `filenames` is only used to render chips on the sent message.
      agentWebSocket.send({
        action: "agent.prompt",
        payload: {
          message: content,
          experiment_id: experimentId
        }
      });
      return true;
    },
    [isStreaming, isHistoryLoading, historyError, experimentId, isReady]
  );

  const stop = useCallback(() => {
    if (!isStreaming || isStopping) {
      return;
    }
    agentWebSocket.send({
      action: "agent.stop",
      payload: { experiment_id: experimentId }
    });
    // Remove the partial assistant bubble and stale extras immediately;
    // agent.turn.done will arrive shortly via the channel layer and trigger the server refetch.
    setLiveMessages(prev => prev.filter(m => m.role !== "assistant"));
    setLiveExtras(undefined);
    setIsStopping(true);
  }, [isStreaming, isStopping, experimentId]);

  // A 403 (feature flag) or 5xx must not propagate as a thrown error; an unknown count only
  // relaxes the local picker hint, and the upload endpoint stays authoritative on the cap.
  const documentsQuery = useQuery({
    ...agentSessionDocumentsOptions(experimentId),
    enabled: Boolean(experimentId),
    throwOnError: false
  });
  const committedDocuments: CommittedDocuments = {
    count: documentsQuery.data?.documents.length ?? 0,
    totalBytes: documentsQuery.data?.documents.reduce((sum, d) => sum + d.size_bytes, 0) ?? 0
  };

  return {
    messages: allMessages,
    liveExtras,
    isStreaming,
    isStopping,
    connectionState,
    send,
    stop,
    isHistoryLoading,
    historyError,
    refetchHistory,
    clearLocal,
    hasRestoredHistory: translateBackendMessages(historyData?.messages ?? []).length > 0,
    agentSessionId,
    committedMessageCount,
    committedDocuments
  };
}
